/**
 * The Shorts rail (F-141, docs/11 §4.2): chips on the board's top bar, one
 * per short, in the order they go out — release day, mood, music coverage
 * and where the short stands — with a popover per chip for what can be
 * changed from here: the title, the release day, the music slot, the state
 * check-offs, and a jump to the short's rows.
 *
 * F-143: a board holds ONE Short now, so the rail is one chip — "Release",
 * its day and its state — whose run is the whole sheet
 * (`shortRunsForSlots`), with no title field (the board's name is the
 * short's) and nothing to drag. A week of five from before keeps all of it.
 *
 * Two sources, one strip. WHEN a short goes out and where it stands is
 * relational (`BoardSummary.shorts`, moved through PATCH like the script
 * date); what is ON the board for it — its rows, its moods, its stems — is
 * the live doc (`shortRuns`). The chip shows both; the popover writes only
 * the first (moods and music are edited in the grid, and the music button
 * opens the same stem dialog the slot chip does).
 *
 * Reorder is a DRAG of the chips, and it moves DATES, never numbers: the
 * slot number is what the grid says on every line of the short, so dragging
 * chip 3 ahead of chip 1 hands Short 3 the Monday (`reorderReleases`).
 * Details expand below the whole row, so the last short stays inside the
 * board and the drawing controls move down with it.
 *
 * F-152: RELEASED IS NOT A CHECK-OFF HERE ANY MORE. A Short goes out when its
 * final cut — a review video linked to the board — is uploaded to YouTube
 * from the desktop app's Review page, and that upload marks the slot
 * released and records its link (the same PATCH this rail uses). So the
 * popover says so instead of offering "Mark released", with an "Open in
 * Review" button where the host has that page (core/host.ts), and a released
 * short shows its YouTube link as text rather than a field to type one into.
 * Undo still steps a released short back to cut.
 */
import {
  bgmColumns,
  reorderReleases,
  shortRunsForSlots,
  slotsInReleaseOrder,
  BGM_MIN_STEMS,
  type BoardSummary,
  type ShortRun,
  type ShortSlot,
  type ShortSlotPatch,
  type ShortState,
} from "@kola/shared";
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";

import { canOpenReleaseInReview, openExternalUrl, openReleaseInReview } from "../../core/host.js";
import type { BoardSession } from "../../core/session.js";
import { CloseButton } from "../../ui/CloseButton.js";
import { DateButton } from "../../ui/DateButton.js";
import { formatDateKeyWithWeekday, todayDateKey } from "../../ui/date-key.js";
import { bgmUi } from "../bgm/store.js";
import { useDocVersion } from "./useDocVersion.js";

const DRAG_MIME = "application/x-kola-short";

export interface ShortsRailProps {
  session: BoardSession;
  summary: BoardSummary;
  /** Null until the doc has synced — the doc half of the rail waits for it. */
  sheetId: string | null;
  /** Grid view only: the music dialog and the jump live on the canvas. */
  gridActive: boolean;
  /** Write the plan (optimistic + rollback live in BoardScreen). */
  onPatch: (patches: ShortSlotPatch[], summaryAfter: BoardSummary, toast: string) => void;
  /** Frame the short's rows on the canvas. */
  onJump: (rowId: string) => void;
}

/** The states the rail can check a short off as — Released comes from Review (F-152). */
type MarkableState = Exclude<ShortState, "released">;

/** The next check-off a short can take from its current state. */
function nextStates(state: ShortState): MarkableState[] {
  switch (state) {
    case "planned":
      return ["shot", "cut"];
    case "shot":
      return ["cut"];
    default:
      return [];
  }
}

const STATE_LABEL: Record<ShortState, string> = {
  planned: "Planned",
  shot: "Shot",
  cut: "Cut",
  released: "Released",
};

const MARK_LABEL: Record<MarkableState, string> = {
  planned: "Back to planned",
  shot: "Mark shot",
  cut: "Mark cut",
};

/** Only a web link is offered as one — `url` is free text from older clients. */
function isWebUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function ShortsRail({ session, summary, sheetId, gridActive, onPatch, onJump }: ShortsRailProps) {
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const version = useDocVersion(session);

  // Close on a press anywhere outside — the "⋯" menu's rule.
  useEffect(() => {
    if (openSlot === null) return;
    const onPointerDown = (event: PointerEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setOpenSlot(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenSlot(null);
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openSlot]);

  const ordered = slotsInReleaseOrder(summary.shorts);
  // F-143: one Short per board — the chip and the popover name it "Short",
  // not "Short 1", and there is no order to drag.
  const single = summary.shorts.length === 1;
  const nameOf = (slot: ShortSlot) => (single ? "Short" : `Short ${slot.slot}`);
  const slotKey = summary.shorts.map((slot) => slot.slot).join(",");
  // The doc half, re-read whenever the doc changes (`version` is the trigger).
  const runs: ShortRun[] = useMemo(
    () =>
      sheetId === null
        ? []
        : shortRunsForSlots(session.board, sheetId, slotKey === "" ? [] : slotKey.split(",").map(Number)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- version IS the dependency
    [session, sheetId, version, slotKey],
  );
  const runOf = (slot: number) => runs.find((run) => run.slot === slot);
  const today = todayDateKey();

  const patchSlots = (patches: ShortSlotPatch[], toast: string) => {
    const nowIso = new Date().toISOString();
    const bySlot = new Map(patches.map((patch) => [patch.slot, patch]));
    const shorts = summary.shorts.map((slot) => {
      const patch = bySlot.get(slot.slot);
      if (patch === undefined) return slot;
      const next: ShortSlot = { ...slot };
      if (patch.title !== undefined) next.title = patch.title.trim();
      if (patch.releaseDate !== undefined) next.releaseDate = patch.releaseDate;
      if (patch.url !== undefined) next.url = patch.url;
      if (patch.state !== undefined && patch.state !== slot.state) {
        next.state = patch.state;
        next.releasedAt = patch.state === "released" ? nowIso : null;
      }
      return next;
    });
    onPatch(patches, { ...summary, shorts }, toast);
  };

  const drop = (targetSlot: number) => {
    if (dragging === null || dragging === targetSlot) return;
    const order = ordered.map((slot) => slot.slot).filter((n) => n !== dragging);
    const at = order.indexOf(targetSlot);
    order.splice(at < 0 ? order.length : at, 0, dragging);
    const patches = reorderReleases(summary.shorts, order);
    if (patches.length > 0) patchSlots(patches, "Release order changed — the release days moved with it.");
  };

  const openShort = ordered.find((slot) => slot.slot === openSlot);

  return (
    <div
      className={`shorts-rail${single ? " shorts-rail-single" : ""}`}
      ref={wrapRef}
      role="group"
      aria-label={single ? "Short" : "Shorts"}
    >
      {ordered.map((slot) => {
        const run = runOf(slot.slot);
        const overdue = slot.state !== "released" && slot.releaseDate < today;
        const isOpen = openSlot === slot.slot;
        const chipTitle = [
          `${nameOf(slot)}${slot.title ? ` · ${slot.title}` : ""}`,
          `${STATE_LABEL[slot.state]} · releases ${formatDateKeyWithWeekday(slot.releaseDate)}`,
          run !== undefined && run.moods.length > 0 ? `Mood: ${run.moods.join(", ")}` : "No mood yet",
        ].join("\n");
        return (
          <div
            key={slot.slot}
            className={`shorts-rail-item${dragging === slot.slot ? " dragging" : ""}${over === slot.slot ? " drag-over" : ""}`}
            onDragOver={(event: DragEvent) => {
              if (!event.dataTransfer.types.includes(DRAG_MIME)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setOver(slot.slot);
            }}
            onDragLeave={() => setOver((prev) => (prev === slot.slot ? null : prev))}
            onDrop={(event: DragEvent) => {
              if (!event.dataTransfer.types.includes(DRAG_MIME)) return;
              event.preventDefault();
              setOver(null);
              drop(slot.slot);
            }}
          >
            <button
              type="button"
              className={`shorts-rail-chip shorts-rail-${slot.state}${overdue ? " shorts-rail-overdue" : ""}`}
              title={chipTitle}
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              draggable={!single}
              onDragStart={(event) => {
                event.dataTransfer.setData(DRAG_MIME, String(slot.slot));
                event.dataTransfer.effectAllowed = "move";
                setDragging(slot.slot);
                setOpenSlot(null);
              }}
              onDragEnd={() => {
                setDragging(null);
                setOver(null);
              }}
              onClick={() => setOpenSlot((prev) => (prev === slot.slot ? null : slot.slot))}
            >
              <span className={`shorts-dot shorts-dot-${slot.state}`} aria-hidden />
              <span className="shorts-rail-num">{single ? "Release" : `Short ${slot.slot}`}</span>
              <span className="shorts-rail-when">{formatDateKeyWithWeekday(slot.releaseDate)}</span>
            </button>

          </div>
        );
      })}
      {openShort !== undefined && (
        <ShortPopover
          key={openShort.slot}
          session={session}
          sheetId={sheetId}
          slot={openShort}
          name={nameOf(openShort)}
          single={single}
          run={runOf(openShort.slot)}
          gridActive={gridActive}
          onOpenInReview={
            canOpenReleaseInReview()
              ? () => openReleaseInReview(summary.id, openShort.slot)
              : undefined
          }
          onClose={() => setOpenSlot(null)}
          onPatch={patchSlots}
          onJump={onJump}
        />
      )}
    </div>
  );
}

function ShortPopover({
  session,
  sheetId,
  slot,
  name,
  single,
  run,
  gridActive,
  onOpenInReview,
  onClose,
  onPatch,
  onJump,
}: {
  session: BoardSession;
  sheetId: string | null;
  slot: ShortSlot;
  /** "Short" on a one-Short board, "Short 3" on a week of five. */
  name: string;
  /** F-143: the board is this short — its name is the title, so no title field. */
  single: boolean;
  run: ShortRun | undefined;
  gridActive: boolean;
  /** F-152: the host's Review page, where a Short is released; absent without one. */
  onOpenInReview: (() => void) | undefined;
  onClose: () => void;
  onPatch: (patches: ShortSlotPatch[], toast: string) => void;
  onJump: (rowId: string) => void;
}) {
  const [title, setTitle] = useState(slot.title);

  const commitTitle = () => {
    const next = title.trim();
    if (next === slot.title) return;
    onPatch([{ slot: slot.slot, title: next }], next ? `${name}: "${next}"` : `${name}: title cleared`);
  };

  // F-152: the link Review's upload recorded, read-only — offered as a link
  // only when it is a web address.
  const link = slot.url !== null && isWebUrl(slot.url) ? slot.url : null;

  // The music slot is the short's first run's top cell in the BGM column —
  // the same target the slot chip on the grid opens.
  const musicTarget = (() => {
    if (sheetId === null || run === undefined) return null;
    const { bgmColId } = bgmColumns(session.board, sheetId);
    if (bgmColId === undefined) return null;
    const label = run.moods.length > 0 ? `${name} · ${run.moods[0]}` : name;
    return { sheetId, rowId: run.topRowId, colId: bgmColId, label };
  })();

  return (
    <div className="shorts-popover" role="dialog" aria-label={name}>
      <div className="shorts-popover-head">
        <span className={`shorts-dot shorts-dot-${slot.state}`} aria-hidden />
        <span className="shorts-popover-title">{name}</span>
        <span className="shorts-popover-state">{STATE_LABEL[slot.state]}</span>
        <CloseButton onClick={onClose} />
      </div>
      {!single && (
        <label className="shorts-popover-field">
          <span className="create-board-label">Title</span>
          <input
            className="text-input"
            value={title}
            placeholder="Working title"
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={commitTitle}
            onKeyDown={(event) => {
              if (event.key === "Enter") (event.target as HTMLInputElement).blur();
              if (event.key === "Escape") onClose();
            }}
          />
        </label>
      )}
      <div className="shorts-popover-row">
        <span className="create-board-label">Release</span>
        <DateButton
          value={slot.releaseDate}
          onChange={(next) => {
            if (next === "" || next === slot.releaseDate) return;
            onPatch(
              [{ slot: slot.slot, releaseDate: next }],
              `${name} releases ${formatDateKeyWithWeekday(next)} — its task follows.`,
            );
          }}
          placeholder="Set release day"
          context={name}
          className="date-button-chip"
        />
      </div>
      <div className="shorts-popover-row">
        <span className="create-board-label">Mood</span>
        <span className="shorts-popover-value">
          {run === undefined || run.moods.length === 0 ? "— (write it in the Mood column)" : run.moods.join(" · ")}
        </span>
      </div>
      <div className="shorts-popover-row">
        <span className="create-board-label">Music</span>
        {run === undefined ? (
          <span className="shorts-popover-value">—</span>
        ) : (
          <button
            type="button"
            className="btn btn-ghost shorts-popover-btn"
            disabled={!gridActive || musicTarget === null}
            title={
              !gridActive
                ? "Switch to Grid to work on the music"
                : musicTarget === null
                  ? "The BGM column appears once the board has synced"
                  : `Open the stems for ${single ? "the Short" : name}`
            }
            onClick={() => {
              if (musicTarget === null) return;
              bgmUi.getState().edit(musicTarget);
              onClose();
            }}
          >
            ♪ {run.music.covered}/{run.music.total} slots with {BGM_MIN_STEMS}+ stems
          </button>
        )}
      </div>
      {slot.state === "released" ? (
        <div className="shorts-popover-row">
          <span className="create-board-label">Link</span>
          {link !== null ? (
            <a
              className="shorts-popover-link"
              href={link}
              title={`Open ${link}`}
              onClick={(event) => {
                event.preventDefault();
                openExternalUrl(link);
              }}
            >
              {link}
            </a>
          ) : (
            <span className="shorts-popover-value">{slot.url ?? "—"}</span>
          )}
        </div>
      ) : (
        <div className="shorts-popover-row shorts-popover-release">
          <span className="shorts-popover-value">Released when its video is uploaded from Review</span>
          {onOpenInReview !== undefined && (
            <button
              type="button"
              className="btn btn-ghost shorts-popover-btn"
              title={`Open Review to upload ${single ? "the Short" : name} to YouTube`}
              onClick={() => {
                onOpenInReview();
                onClose();
              }}
            >
              Open in Review
            </button>
          )}
        </div>
      )}
      <div className="shorts-popover-actions">
        {nextStates(slot.state).map((state) => (
          <button
            key={state}
            type="button"
            className="btn"
            onClick={() => onPatch([{ slot: slot.slot, state }], `${name}: ${STATE_LABEL[state].toLowerCase()}`)}
          >
            {MARK_LABEL[state]}
          </button>
        ))}
        {slot.state !== "planned" && (
          <button
            type="button"
            className="btn btn-ghost"
            // Released is only ever set by an upload from Review, so say how it comes back.
            title={
              slot.state === "released"
                ? "Back to Cut. To mark it released again, pick Released on its video in Review."
                : undefined
            }
            onClick={() => {
              const back: ShortState =
                slot.state === "released" ? "cut" : slot.state === "cut" ? "shot" : "planned";
              onPatch([{ slot: slot.slot, state: back }], `${name}: back to ${STATE_LABEL[back].toLowerCase()}`);
            }}
          >
            Undo
          </button>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-ghost"
          disabled={!gridActive || run === undefined}
          title={gridActive ? (single ? "Frame the script's first row" : "Frame this short's rows") : "Switch to Grid to jump to the rows"}
          onClick={() => {
            if (run !== undefined) onJump(run.topRowId);
            onClose();
          }}
        >
          Jump to rows
        </button>
      </div>
    </div>
  );
}
